import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Pressable, Text, useTheme } from "native-base";
import { Circle, G, Path, Rect, Svg, Text as SvgText } from "react-native-svg";
import { isWeb } from "@madmedical/utils";
import useResponsive from "../util/useResponsive";
import { useChart } from "../providers/chart";
import { dayList, dayListFull } from "../util/calendar";
import InsulinTooltip from "./InsulinTooltip";
const GraphDivision = ({ width, isSolo = false, isLast = false, }) => (_jsxs(Box, Object.assign({ borderTopWidth: 2, borderColor: "gray.100", borderLeftWidth: isSolo ? 0 : 2, borderLeftStyle: "dashed", borderRightWidth: isLast ? 2 : 0, borderRightStyle: "dashed", width: width, height: "256px" }, { children: [_jsx(Box, { borderBottomWidth: 1, borderBottomColor: "gray.100", height: "25%" }), _jsx(Box, { borderBottomWidth: 1, borderBottomColor: "gray.100", height: "25%" }), _jsx(Box, { borderBottomWidth: 1, borderBottomColor: "gray.100", height: "25%" }), _jsx(Box, { borderBottomWidth: 2, borderBottomColor: "gray.100", height: "25%" })] })));
const InsulinPortioningGraph = ({ pointData }) => {
    var _a, _b;
    const { isSmallScreen } = useResponsive();
    const { breakpoints } = useTheme();
    const { dateRange } = useChart();
    const getWeeksInMonthFromDate = useMemo(() => {
        const date = dateRange.from;
        const dateTo = dateRange.to;
        const millisecondsPerDay = 1000 * 60 * 60 * 24;
        const differenceInMilliseconds = Math.abs(dateTo.getTime() - date.getTime());
        const differenceInDays = differenceInMilliseconds / millisecondsPerDay;
        if (differenceInDays < 7) {
            return 1;
        }
        const month = date.getMonth();
        const year = date.getFullYear();
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);
        const firstWeekday = firstDayOfMonth.getDay() || 7;
        const totalDaysInMonth = lastDayOfMonth.getDate();
        return Math.ceil((totalDaysInMonth + firstWeekday - 1) / 7);
    }, [dateRange.from, dateRange.to]);
    const weekCount = getWeeksInMonthFromDate;
    const graphWidth = isWeb
        ? ((_b = (_a = document.getElementById("portioning-graph")) === null || _a === void 0 ? void 0 : _a.clientWidth) !== null && _b !== void 0 ? _b : 1000) -
            70
        : 320;
    const basicBlock = graphWidth / 7;
    const divideWidth = graphWidth / (weekCount * 7);
    const offsetRatio = Math.round(divideWidth / 1.6);
    const offsetX = graphWidth < breakpoints.md ? 90 : offsetRatio;
    // correct tot the BE type?
    const daysOfWeekDesktop = dayListFull;
    const daysOfWeekMobile = dayList;
    const weekNumbers = [
        "1.",
        "5.",
        "10.",
        "15.",
        "20.",
        "25.",
        "30.",
    ];
    /*
    const daysDividing: string[] = Array(weekCount)
        .fill([...daysOfWeekMobile])
        .flat() as string[];
    */
    const calculateCoordinate = useCallback((value) => value + 110, []);
    // insert the calculated coordinates into the path
    const pathCoordinates = `M0,${calculateCoordinate(pointData[0].y)} ${pointData
        .map((item, index) => index > 0
        ? `L${divideWidth * index + offsetX},${calculateCoordinate(item.y)}`
        : `L${offsetX},${calculateCoordinate(pointData[0].y)}`)
        .join(" ")}
        L${divideWidth * pointData.length + offsetX + graphWidth / 7},${calculateCoordinate(pointData[pointData.length - 1].y)}`;
    /*
    L${offsetX},${calculateCoordinate(pointData[0].y)} L${
        divideWidth + offsetX
    },${calculateCoordinate(pointData[1].y)} L${
        divideWidth * 2 + offsetX
    },${calculateCoordinate(pointData[2].y)} L${
        divideWidth * 3 + offsetX
    },${calculateCoordinate(pointData[3].y)} L${
        divideWidth * 4 + offsetX
    },${calculateCoordinate(pointData[4].y)} L${
        divideWidth * 5 + offsetX
    },${calculateCoordinate(pointData[5].y)} L${
        divideWidth * 6 + offsetX
    },${calculateCoordinate(pointData[6].y)} L${
        divideWidth * 7 + offsetX
    },${calculateCoordinate(pointData[6].y)}`;
    */
    const pathStroke = "#8D39B5";
    const maxValue = Math.max(...pointData.map((item) => (item.value ? Math.ceil(item.value) : 0)));
    const maxInsertions = Math.max(...pointData.map((item) => {
        const { insertions } = item;
        return Object.values(insertions).reduce((acc, curr) => acc + curr, 0);
    }));
    const svgSpaceHeight = 230;
    const barWidth = 28 / weekCount;
    const proportionalValue = svgSpaceHeight / maxInsertions;
    const showInsertGraph = useCallback((insulinProduct) => {
        const { insertions } = insulinProduct;
        const numberOfInsertionTypes = Object.values(insertions).filter((item) => item > 0).length;
        const calculatedValues = Object.entries(insertions).reduce((acc, curr) => {
            const height = Math.ceil(proportionalValue * curr[1]);
            const shape = {
                height,
                y: Math.ceil(acc.yCoord - height),
                type: curr[0],
            };
            acc.shapes.push(shape);
            acc.yCoord -= height;
            return acc;
        }, {
            shapes: [],
            yCoord: svgSpaceHeight,
        });
        return (_jsx(_Fragment, { children: calculatedValues.shapes.reverse().map((item, index) => {
                if ((item === null || item === void 0 ? void 0 : item.type) === "other") {
                    return (_jsxs(Fragment, { children: [_jsx(Rect, { width: barWidth, height: item.height, fill: "#A9B5BB", y: item.y }), item.height > 0 ? (_jsx(Rect, { width: barWidth, rx: 5, ry: 5, height: 10, fill: "#A9B5BB", y: item.y - 5 })) : null] }, `other-${index}`));
                }
                if (item.type === "basal") {
                    return (_jsxs(Fragment, { children: [_jsx(Rect, { width: barWidth, height: item.height, fill: "#8DBBFA", y: item.y }), numberOfInsertionTypes === 1 &&
                                item.height ? (_jsx(Rect, { width: barWidth, rx: 5, ry: 5, height: 10, fill: "#8DBBFA", y: item.y - 5 })) : null] }, `basal-${index}`));
                }
                if (item.type === "rapid") {
                    return (_jsxs(Fragment, { children: [_jsx(Rect, { width: barWidth, height: item.height, fill: "#418DFF", y: item.y }), numberOfInsertionTypes === 1 &&
                                item.height ? (_jsx(Rect, { width: barWidth, rx: 5, ry: 5, height: 10, fill: "#418DFF", y: item.y - 5 })) : null] }, `rapid-${index}`));
                }
            }) }));
    }, [barWidth, proportionalValue]);
    const [tooltip, setTooltip] = useState({
        bloodSugar: 0,
        insertions: {
            rapid: 0,
            basal: 0,
            other: 0,
        },
        administeredAt: new Date(),
        status: "OK",
        onShow: false,
    });
    const showTooltip = useCallback((event, point) => {
        var _a, _b, _c;
        const toolTipOffset = 100;
        const originalLeft = event.nativeEvent.pageX - (isSmallScreen ? -35 : 200);
        const left = originalLeft < toolTipOffset ? 150 : originalLeft;
        const { value, insertions, administeredAt, status } = point;
        setTooltip({
            bloodSugar: value,
            insertions: {
                rapid: (_a = insertions === null || insertions === void 0 ? void 0 : insertions.rapid) !== null && _a !== void 0 ? _a : 0,
                basal: (_b = insertions.basal) !== null && _b !== void 0 ? _b : 0,
                other: (_c = insertions.other) !== null && _c !== void 0 ? _c : 0,
            },
            administeredAt: new Date(administeredAt),
            status,
            onShow: true,
            bottom: 70,
            left,
        });
    }, [isSmallScreen]);
    useEffect(() => {
        setTooltip((state) => (Object.assign(Object.assign({}, state), { onShow: false })));
    }, []);
    return (_jsxs(Box, Object.assign({ w: "100%", overflow: "hidden", paddingBottom: "24px", nativeID: "portioning-graph" }, { children: [_jsx(Pressable, Object.assign({ onPress: () => setTooltip((state) => (Object.assign(Object.assign({}, state), { onShow: false }))) }, { children: _jsxs(Box, Object.assign({ position: "relative", 
                    // width={isSmallScreen ? "410px" : "1000px"}
                    background: "white", paddingBottom: "24px" }, { children: [_jsx(Box, Object.assign({ ml: "25px", mb: "-14px", display: "flex", alignItems: "center", flexDirection: "row", width: "100%" }, { children: daysOfWeekMobile === null || daysOfWeekMobile === void 0 ? void 0 : daysOfWeekMobile.map((day, iDay) => (_jsx(Text, Object.assign({ fontSize: "xs", color: "gray.600", paddingTop: "6px", marginBottom: "-6px", width: basicBlock }, { children: graphWidth > breakpoints.md && weekCount === 1
                                    ? daysOfWeekDesktop[iDay]
                                    : weekCount > 1
                                        ? weekNumbers[iDay]
                                        : day }), day))) })), _jsxs(Box, Object.assign({ position: "absolute", right: graphWidth > breakpoints.md && weekCount === 1
                                ? "15px"
                                : "5px", textAlign: graphWidth > breakpoints.md && weekCount === 1
                                ? "right"
                                : "left", zIndex: 0 }, { children: [_jsx(Text, Object.assign({ fontSize: "xs", color: "gray.600", paddingTop: "6px", marginBottom: "6px", width: graphWidth > breakpoints.md && weekCount === 1
                                        ? `48px`
                                        : `40px` }, { children: graphWidth > breakpoints.md && weekCount === 1
                                        ? daysOfWeekDesktop[0]
                                        : weekCount > 1
                                            ? weekNumbers[0]
                                            : daysOfWeekMobile[0] })), _jsx(GraphDivision, { width: graphWidth > breakpoints.md && weekCount === 1
                                        ? `48px`
                                        : `40px`, isSolo: true })] })), _jsx(Box, Object.assign({ position: "absolute", zIndex: -1, top: "0", left: "24px", paddingTop: "30px", w: "100%", h: "100%", display: "flex", flexDirection: "row", borderRightStyle: "dashed", borderRightWidth: 2 }, { children: daysOfWeekMobile.map((day, index) => (_jsx(GraphDivision, { width: basicBlock, isLast: index === daysOfWeekMobile.length - 1 }, `${day}-${index}`))) })), _jsxs(Box, Object.assign({ position: "absolute", top: "30px", left: "12px" }, { children: [_jsxs(Box, Object.assign({ h: "255px", w: "24px", display: "flex", flexDirection: "column", justifyContent: "space-between" }, { children: [_jsx(Box, Object.assign({ background: "white", shadow: "1", borderRadius: 4 }, { children: _jsx(Text, Object.assign({ fontSize: "xs", color: "blue.500", textAlign: "center" }, { children: maxInsertions })) })), _jsx(Box, Object.assign({ background: "white", shadow: "1", borderRadius: 4 }, { children: _jsx(Text, Object.assign({ fontSize: "xs", color: "blue.500", textAlign: "center" }, { children: maxInsertions / 2 })) })), _jsx(Box, Object.assign({ background: "white", shadow: "1", borderRadius: 4 }, { children: _jsx(Text, Object.assign({ fontSize: "xs", color: "blue.500", textAlign: "center" }, { children: "0" })) }))] })), _jsx(Box, Object.assign({ background: "white", px: 1, shadow: "1", borderRadius: 4, mt: "10px" }, { children: _jsx(Text, Object.assign({ fontSize: "xs", color: "blue.500" }, { children: "Egys\u00E9g" })) }))] })), _jsx(Svg, Object.assign({ width: graphWidth > 1000 ? graphWidth : 1000, height: "300", viewBox: `0 0 ${graphWidth > 1000 ? graphWidth : 1000} 300`, fill: "none", preserveAspectRatio: "xMaxYMin meet" }, { children: _jsxs(G, Object.assign({ x: isSmallScreen ? (weekCount === 1 ? 50 : 0) : 0, y: "0" }, { children: [_jsx(G, Object.assign({ x: weekCount === 1
                                            ? isSmallScreen
                                                ? offsetRatio - 43
                                                : offsetRatio - 5
                                            : isSmallScreen
                                                ? 25
                                                : 25 }, { children: pointData.map((day, i) => (_jsx(G, Object.assign({ x: i === 0 ? 0 : i * divideWidth, y: "50" }, { children: showInsertGraph(day) }), `insugraph-${i}`))) })), _jsx(G, Object.assign({ x: weekCount === 1
                                            ? isSmallScreen
                                                ? offsetX - 180
                                                : 10
                                            : isSmallScreen
                                                ? offsetX - 150
                                                : 0 }, { children: _jsx(Path, { d: pathCoordinates, fill: "none", stroke: pathStroke, strokeWidth: 2 }) })), _jsx(G, Object.assign({ x: weekCount === 1
                                            ? isSmallScreen
                                                ? offsetX - 180
                                                : offsetRatio - 80
                                            : isSmallScreen
                                                ? offsetX - 150
                                                : offsetX - 90 }, { children: pointData.map((point, i) => (_jsxs(G, Object.assign({ x: i === 0 ? 0 : i * divideWidth, y: point.y }, { children: [weekCount === 1 && (_jsx(Rect, { x: "80", y: "90", width: "22", height: "16", rx: "4", fill: "#8D39B5" })), _jsx(Circle, { cx: "90", cy: "110", r: "4.5", fill: point.status === "ALERT"
                                                        ? "RED"
                                                        : point.status === "OK"
                                                            ? "#4CC8B0"
                                                            : "#FFCF47", stroke: "#8D39B5", strokeWidth: "2" }), weekCount === 1 && (_jsx(SvgText, Object.assign({ fill: "white", fontSize: "10px", fontFamily: "Inter", x: "84", y: "102", textAnchor: "start" }, { children: point.value
                                                        ? point.value.toFixed(1)
                                                        : "0.0" })))] }), `points-${point.y}-${i}`))) }))] })) })), _jsx(Box, Object.assign({ position: "absolute", top: "30px", left: "24px", width: "100%", height: "100%" }, { children: pointData.map((point, i) => (_jsx(Pressable, { onPress: (e) => showTooltip(e, point), position: "absolute", left: (i === 0 ? 0 : i * divideWidth) +
                                    (isSmallScreen
                                        ? i === 0
                                            ? 4
                                            : i === 1 // fix the strange bug that breaks second pressable
                                                ? 13
                                                : 14
                                        : offsetRatio - 40 / weekCount), bottom: "70px", width: `${32 / weekCount}px`, height: 260 }, `pressable-${point.y}-${i}`))) })), _jsxs(Box, Object.assign({ position: "absolute", top: "30px", right: graphWidth < breakpoints.md ? "10px" : "35px", alignItems: "flex-end" }, { children: [_jsxs(Box, Object.assign({ h: "255px", w: "24px", display: "flex", flexDirection: "column", justifyContent: "space-between" }, { children: [_jsx(Box, Object.assign({ background: "white", shadow: "1", borderRadius: 4 }, { children: _jsx(Text, Object.assign({ fontSize: "xs", color: "violet.600", textAlign: "center" }, { children: maxValue })) })), _jsx(Box, Object.assign({ background: "white", shadow: "1", borderRadius: 4 }, { children: _jsx(Text, Object.assign({ fontSize: "xs", color: "violet.600", textAlign: "center" }, { children: maxValue / 2 })) })), _jsx(Box, Object.assign({ background: "white", shadow: "1", borderRadius: 4 }, { children: _jsx(Text, Object.assign({ fontSize: "xs", color: "violet.600", textAlign: "center" }, { children: "0" })) }))] })), _jsx(Box, Object.assign({ background: "white", px: 1, shadow: "1", borderRadius: 4, mt: "10px" }, { children: _jsx(Text, Object.assign({ fontSize: "xs", color: "violet.600" }, { children: "mmol/l" })) }))] }))] })) })), _jsx(InsulinTooltip, { tooltip: tooltip })] })));
};
export default InsulinPortioningGraph;
